import { useAuth } from "@dl/web-core-js/auth";
import { Button, Dialog, Field, MultiSelect, Select } from "@dl/web-core-js/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { refusalMessage } from "../../api/manufacturing";
import { ProductClient, WarehouseClient, type DispatchBulkMode, type LazyProductDTO, type Market } from "../../api/product";

const KEEP = "KEEP";
type Choice = typeof KEEP | DispatchBulkMode;

/**
 * One change to the dispatch warehouses of the products selected in the grid,
 * per market: keep, set the list, add warehouses or remove them. Setting a
 * market to no warehouse means the products are not sold there, as on a
 * product's Dispatch tab. Bundles in the selection are skipped: their dispatch
 * is derived from their items.
 */
export function DispatchBulkDialog({
  open,
  products,
  onClose,
  onDone,
}: {
  open: boolean;
  /** The grid's selection. */
  products: LazyProductDTO[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ProductClient(http), [http]);
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
    enabled: open,
  });
  const options = (warehouses.data ?? [])
    .filter((warehouse) => warehouse.is_active && warehouse.kind === "DISTRIBUTION")
    .map((warehouse) => ({ value: String(warehouse.id), label: `${warehouse.code} · ${warehouse.name ?? ""}`.trim() }));

  const [mode, setMode] = useState<Record<Market, Choice>>({ CA: KEEP, US: KEEP });
  const [picked, setPicked] = useState<Record<Market, string[]>>({ CA: [], US: [] });

  // A fresh form every time: a change left over from the last selection is the mistake to prevent.
  useEffect(() => {
    if (open) {
      setMode({ CA: KEEP, US: KEEP });
      setPicked({ CA: [], US: [] });
    }
  }, [open]);

  const bundles = products.filter((product) => product.type === "BUNDLE").length;
  const markets: Array<{ market: Market; label: string }> = [
    { market: "CA", label: t("product.dispatch.ca", "Canada") },
    { market: "US", label: t("product.dispatch.us", "United States") },
  ];
  const active = markets.filter(({ market }) => mode[market] !== KEEP);
  const missing = active.some(({ market }) => mode[market] !== "SET" && picked[market].length === 0);
  const notSold = active.filter(({ market }) => mode[market] === "SET" && picked[market].length === 0);

  const apply = useMutation({
    mutationFn: () =>
      client.dispatchBulk({
        product_ids: products.map((product) => product.id),
        changes: Object.fromEntries(
          active.map(({ market }) => [market, { mode: mode[market] as DispatchBulkMode, warehouse_ids: picked[market].map(Number) }]),
        ),
      }),
    onSuccess: (result) => {
      toast.success(
        t("products.dispatch.applied", "Dispatch changed on {{changed}} products; {{unchanged}} already had it, {{bundles}} bundles skipped.", {
          changed: result.changed,
          unchanged: result.unchanged,
          bundles: result.bundles_skipped,
        }),
      );
      onDone();
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const modeOptions = [
    { value: KEEP, label: t("product.rules.keep", "Keep as is") },
    { value: "SET", label: t("products.dispatch.mode.set", "Set to") },
    { value: "ADD", label: t("products.dispatch.mode.add", "Add") },
    { value: "REMOVE", label: t("products.dispatch.mode.remove", "Remove") },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("products.dispatch.title", "Change the dispatch warehouses of {{count}} products", { count: products.length })}
      description={t(
        "products.dispatch.description",
        "Per market: set the warehouses each product ships from, add some, or remove some. A market left on \"Keep as is\" keeps what each product has.",
      )}
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={apply.isPending}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={() => apply.mutate()}
            disabled={active.length === 0 || missing || products.length === bundles}
            loading={apply.isPending}
          >
            {t("button.apply", "Apply")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {markets.map(({ market, label }) => (
          <div key={market} className="grid gap-3 sm:grid-cols-[10rem_1fr]">
            <Field label={label} htmlFor={`bulk-dispatch-${market}`}>
              <Select
                id={`bulk-dispatch-${market}`}
                value={mode[market]}
                options={modeOptions}
                onChange={(event) => setMode({ ...mode, [market]: event.target.value as Choice })}
              />
            </Field>
            <Field label={t("product.dispatch.warehouses", "Dispatch warehouses")} htmlFor={`bulk-dispatch-${market}-warehouses`}>
              <MultiSelect
                id={`bulk-dispatch-${market}-warehouses`}
                disabled={mode[market] === KEEP}
                options={options}
                value={picked[market]}
                onChange={(ids: string[]) => setPicked({ ...picked, [market]: ids })}
                placeholder={
                  mode[market] === "SET"
                    ? t("product.dispatch.none", "Not sold in this market")
                    : t("products.dispatch.pick", "Pick warehouses")
                }
              />
            </Field>
          </div>
        ))}
        <p className="text-[12px] text-ink-muted">
          {notSold.length > 0 &&
            t("products.dispatch.not-sold", "{{markets}}: no warehouse means the products stop being sold there. ", {
              markets: notSold.map(({ label }) => label).join(", "),
            })}
          {bundles > 0 &&
            t("products.dispatch.bundles", "{{count}} bundles in the selection are left alone: their dispatch comes from their items.", {
              count: bundles,
            })}
        </p>
      </div>
    </Dialog>
  );
}
